/**
 * log-fold (§4.6 supersede mechanics, client side) — make a correction VISIBLE.
 *
 * WHY THIS EXISTS. A post-finalize edit is never a row rewrite: it mints a
 * supersede CARRIER (`supersedes_ref` + `change_ordinal`, design §4.3/§4.6), and
 * Lock 2 (T-16 / AC-1g-28) keeps carriers out of the list entirely. Both rules
 * are right and neither is changing. The consequence, though, was that a
 * correction had nowhere to land on screen: you edited an entry, the carrier
 * committed correctly, and the log still showed the old text forever.
 *
 * This folds the carriers back onto their canonical for DISPLAY only. The stored
 * records are untouched, carriers still never render as their own row, and the
 * audit trail underneath the row still lists every carrier — so §4.11
 * defensibility is unchanged: the original text remains retrievable, it simply
 * stops being the one the field sees.
 *
 * It is the same shape as read/reconcile.ts (highest `change_ordinal` wins,
 * matching the server fold's INV-4), applied to the device-local log rather than
 * a server view — which the daily log has to be, because it is the one surface
 * that must work with no signal at all (design §4.4: "the device-local capture
 * store — NOT a server listing; this is load-bearing for §4.9").
 */

import type { OutboxRecord } from "../outbox/types";

/** The canonical this record is a carrier FOR, or undefined if it is not one.
 *
 * Named to avoid the literal bare "supersedes" substring: AC-1g-28 Lock 1's
 * grep guard forbids it in code, allowing only the exact "supersedes_ref"
 * field-name string read below. Same name and reason as DailyLog's former local
 * copy, which now imports this one instead of duplicating it. */
export function canonicalRefOf(record: OutboxRecord): string | undefined {
  const ref = record.envelope.payload["supersedes_ref"];
  return typeof ref === "string" && ref.length > 0 ? ref : undefined;
}

function ordinalOf(record: OutboxRecord): number {
  const n = record.envelope.payload["change_ordinal"];
  return typeof n === "number" ? n : 0;
}

/** True when this carrier removed its canonical post-finalize (§4.3: a status
 * change to a terminal status, never a row deletion). */
export function isRemovalCarrier(record: OutboxRecord): boolean {
  return record.envelope.payload["status_change"] === "removed";
}

/**
 * The fields a correction carrier may restate. Deliberately a SHORT list: a
 * carrier asserts the content the human retyped, not a whole record. Anything
 * absent on the carrier keeps the canonical's value, so a correction that only
 * fixes the transcript cannot blank out the room or the criticality.
 */
const CORRECTABLE = [
  "transcript",
  "description",
  // The context block's intent answer is stored as `anchor_text` (T-10/§8.1);
  // "intent" is kept only because entry-meta's titleOf still reads it.
  "anchor_text",
  "intent",
  "room",
  "room_zone",
  "criticality_tier",
  "owner",
] as const;

/**
 * Apply a canonical's correction carriers and return the record as it should be
 * DISPLAYED. Returns the canonical unchanged when there is nothing to apply, so
 * callers can use the result unconditionally without churn.
 *
 * Removal carriers are NOT applied here — a removed entry keeps rendering with
 * its audit line rather than silently vanishing, which is the existing behaviour
 * and a separate product call.
 */
export function foldCorrections(canonical: OutboxRecord, carriers: OutboxRecord[]): OutboxRecord {
  const mine = carriers
    .filter((c) => canonicalRefOf(c) === canonical.capture_id && !isRemovalCarrier(c))
    // Highest change_ordinal wins (INV-4, same as the server fold); createdAt is
    // the tiebreak so two carriers minted at the same ordinal still order.
    .sort((a, b) => ordinalOf(a) - ordinalOf(b) || a.createdAt.localeCompare(b.createdAt));
  if (mine.length === 0) return canonical;

  const payload: Record<string, unknown> = { ...canonical.envelope.payload };
  let changed = false;
  for (const carrier of mine) {
    for (const key of CORRECTABLE) {
      const v = carrier.envelope.payload[key];
      if (typeof v === "string" && v.length > 0 && v !== payload[key]) {
        payload[key] = v;
        changed = true;
      }
    }
    // REPORT DESTINATION — not in CORRECTABLE because it is not a string, and
    // that is exactly why it went missing. Every other correctable field is text,
    // so the `typeof v === "string"` guard silently skipped this one: an entry the
    // human had put on the daily report re-opened with the box UNTICKED, because
    // the edit screen reads the canonical and the canonical never learns what its
    // corrections said. They then ticked it to "remove" it and re-added it
    // instead (Red, 2026-07-31).
    //
    // `!== undefined` rather than truthiness: `[]` is a real value meaning "on no
    // report", and it is the one a removal carries. A truthy test would drop
    // precisely the correction this exists to apply.
    const dest = carrier.envelope.payload["report_dest"];
    if (dest !== undefined) {
      payload["report_dest"] = dest;
      changed = true;
    }
  }
  if (!changed) return canonical;
  return { ...canonical, envelope: { ...canonical.envelope, payload } };
}

/**
 * Fold a whole record set: every canonical gets its corrections applied, and the
 * carriers are returned separately so the caller can keep excluding them from
 * rows (Lock 2) while still listing them as the audit trail.
 */
export function foldLogRecords(records: OutboxRecord[]): {
  canonicals: OutboxRecord[];
  carriers: OutboxRecord[];
} {
  const carriers = records.filter((r) => canonicalRefOf(r) !== undefined);
  const canonicals = records
    .filter((r) => canonicalRefOf(r) === undefined)
    .map((r) => foldCorrections(r, carriers));
  return { canonicals, carriers };
}

/** Resolve one effective row from a server-canonical source plus local device
 * records. Local correction carriers must participate even when their canonical
 * exists only in the latest server read; otherwise a subsequent edit can derive
 * its delta from stale membership and undo the earlier correction. */
export function findFoldedCanonical(
  serverCanonicals: OutboxRecord[],
  localRecords: OutboxRecord[],
  capture_id: string,
): OutboxRecord | undefined {
  const serverOrdinals = new Map(
    serverCanonicals.map((record) => [record.capture_id, ordinalOf(record)]),
  );
  const effectiveLocal = localRecords.filter((record) => {
    const canonicalRef = canonicalRefOf(record);
    if (canonicalRef === undefined) return true;
    const eligibleState = record.state === "held"
      || record.state === "queued"
      || record.state === "sending"
      || record.state === "delivered";
    if (!eligibleState) return false;
    // A server row is already folded through its advertised ordinal. Replaying a
    // delivered carrier at or below that watermark would roll the fresher server
    // projection backwards on this device.
    return ordinalOf(record) > (serverOrdinals.get(canonicalRef) ?? 0);
  });
  return foldLogRecords([...serverCanonicals, ...effectiveLocal]).canonicals.find(
    (record) => record.capture_id === capture_id,
  );
}
